import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Box, Button, Divider as MuiDivider, styled, Switch, Typography } from '@mui/material';

import { ActionPosition, HeadDialog } from 'Components';
import type { CourseModel } from 'Courses/data';
import {
  saveIsFreshStartMachineReadyToClone,
  saveParticipantsMachineControlActive,
  saveShowOnlyActiveMachines,
  saveWhetherTrainerAllowedPreview
} from 'Courses/data';
import { urls } from 'Router';
import { useAppDispatch } from 'Store';
import { Colors } from 'Themes/colors';
import { Spacing } from 'Themes/spacing';
import { Typo } from 'Themes/typo';
import { copyToClipboard } from 'Utils/copyToClipboard';
import { useToggle } from 'Utils/customHooks';

import type {
  ISaveIsFreshStartMachineReadyToCloneRequest,
  ISaveParticipantsMachineControlActiveRequest,
  ISaveShowOnlyActiveMachinesRequest,
  ISaveWhetherTrainerAllowedPreviewRequest
} from '@libs/models';
import {
  DeleteIcon,
  EditIcon,
  InsertLinkIcon,
  PlayCircleIcon,
  RemoveCircleIcon,
  SettingsIcon,
  TuneIcon
} from '@libs/shared/ui/icons';
import { BaseModal } from '@/libs/shared/ui';

const MainContainer = styled(Box)`
  position: relative;
`;

const SettingsButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${Spacing.small};
`;

const Options = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: ${Colors.white};
  gap: ${Spacing.small};
`;

const SwitchContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.small};
  cursor: pointer;
  & > *:hover {
    text-shadow: 0 0 1px black;
  }
`;

const SwitchLabel = styled(Typography)`
  flex: 1;
  color: ${Colors.black};
  text-wrap: nowrap;
`;

const Divider = styled(MuiDivider)`
  margin: 8px 0 !important;
`;

const StyledInsertLinkIcon = styled(InsertLinkIcon)`
  transform: rotateZ(138deg);
`;

const ShowLinkContent = styled(Box)`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: ${Spacing.medium};
  overflow-wrap: anywhere;
`;

const BigTypography = styled(Typography)`
  color: ${Colors.black} !important;
  font-size: 25px !important;
  font-weight: 700 !important;
`;

type Props = {
  onStartParticipantsMachines: () => void;
  onStopParticipantsMachines: () => void;
  onRemoveParticipantsMachines: () => void;
  onEditCourse: () => void;
  shouldShowStartParticipantsMachines: boolean;
  shouldShowStopParticipantsMachines: boolean;
  shouldShowRemoveParticipantsMachines: boolean;
  canEditCourse: boolean;
  joinLink: string;
  course?: CourseModel;
};

export const SettingsButtonModal = ({
  onStartParticipantsMachines,
  onStopParticipantsMachines,
  onRemoveParticipantsMachines,
  onEditCourse,
  shouldShowStartParticipantsMachines,
  shouldShowStopParticipantsMachines,
  shouldShowRemoveParticipantsMachines,
  canEditCourse,
  joinLink,
  course
}: Readonly<Props>) => {
  const [showOptions, toggleOptions] = useToggle();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleCopyJoinLink = () => {
    copyToClipboard(joinLink);
    toast(t('SuccessfullyCopiedToTheClipboard'));
  };

  const [showJoinLink, toggleShowJoinLink] = useToggle();

  const handleShowLink = () => {
    toggleShowJoinLink();
    toggleOptions();
  };

  const handleShowOnlyActiveMachinesToggle = () => {
    if (!course) {
      return;
    }

    dispatch(
      saveShowOnlyActiveMachines({
        courseId: course.id,
        showOnlyActiveMachines: !(course.showOnlyActiveMachines ?? false)
      } as ISaveShowOnlyActiveMachinesRequest)
    );
  };

  const handleParticipantsMachineControlToggle = () => {
    if (!course) {
      return;
    }

    dispatch(
      saveParticipantsMachineControlActive({
        courseId: course.id,
        participantsMachineControlActive: !(course.participantsMachineControlActive ?? false)
      } as ISaveParticipantsMachineControlActiveRequest)
    );
  };

  const handleTrainerAllowedPreviewChange = () => {
    if (!course) {
      return;
    }

    dispatch(
      saveWhetherTrainerAllowedPreview({
        courseId: course.id,
        whetherTrainerAllowedPreview: !(course.whetherTrainerAllowedPreview ?? false)
      } as ISaveWhetherTrainerAllowedPreviewRequest)
    );
  };

  const handleFreshStartReadyToCloneChange = () => {
    if (!course) {
      return;
    }

    dispatch(
      saveIsFreshStartMachineReadyToClone({
        courseId: course.id,
        isFreshStartMachineReadyToClone: !(course.isFreshStartMachineReadyToClone ?? false)
      } as ISaveIsFreshStartMachineReadyToCloneRequest)
    );
  };

  const handleFreshStartConfigurationClick = () => {
    if (!course) {
      return;
    }

    toggleOptions();
    navigate(urls.courseSettingsPage(course.id));
  };

  const handleEditCourseClick = () => {
    toggleOptions();
    onEditCourse();
  };

  return (
    <MainContainer>
      <SettingsButton variant="outlined" onClick={toggleOptions} startIcon={<SettingsIcon />}>
        {t('coursePanel.settingsMenu')}
      </SettingsButton>

      {showOptions && (
        <BaseModal open={true} onClose={toggleOptions} variant="dropdown" dropdownPosition="left">
          <Options>
            <SwitchContainer onClick={handleShowOnlyActiveMachinesToggle}>
              <Switch
                size="small"
                checked={course?.showOnlyActiveMachines ?? false}
                onChange={handleShowOnlyActiveMachinesToggle}
                onClick={e => e.stopPropagation()}
              />
              <SwitchLabel>{t('coursePanel.showOnlyActiveMachines')}</SwitchLabel>
            </SwitchContainer>

            <SwitchContainer onClick={handleParticipantsMachineControlToggle}>
              <Switch
                size="small"
                checked={course?.participantsMachineControlActive ?? false}
                onChange={handleParticipantsMachineControlToggle}
                onClick={e => e.stopPropagation()}
              />
              <SwitchLabel>{t('coursePanel.participantsMachineControlAlwaysActive')}</SwitchLabel>
            </SwitchContainer>

            {course?.hasFreshStart && (
              <SwitchContainer onClick={handleTrainerAllowedPreviewChange}>
                <Switch
                  size="small"
                  checked={course?.whetherTrainerAllowedPreview ?? false}
                  onChange={handleTrainerAllowedPreviewChange}
                  onClick={e => e.stopPropagation()}
                />
                <SwitchLabel>{t('SharePreviewOfMyMachine')}</SwitchLabel>
              </SwitchContainer>
            )}

            <Divider />

            {course?.hasFreshStart && (
              <>
                <SwitchContainer onClick={handleFreshStartReadyToCloneChange}>
                  <Switch
                    size="small"
                    checked={course?.isFreshStartMachineReadyToClone ?? false}
                    onChange={handleFreshStartReadyToCloneChange}
                    onClick={e => e.stopPropagation()}
                  />
                  <SwitchLabel>{t('FreshStartMachineIsReadyToClone')}</SwitchLabel>
                </SwitchContainer>

                <ActionPosition
                  onClick={handleFreshStartConfigurationClick}
                  icon={<TuneIcon style={{ color: Colors.green }} />}
                  text={t('coursePanel.freshStartTemplateConfiguration')}
                />
              </>
            )}

            <ActionPosition
              onClick={handleShowLink}
              icon={<StyledInsertLinkIcon style={{ color: Colors.green }} />}
              text={t('coursePanel.showJoinLink')}
            />

            <Divider />

            {shouldShowStartParticipantsMachines && (
              <ActionPosition
                onClick={onStartParticipantsMachines}
                icon={<PlayCircleIcon style={{ color: Colors.green }} />}
                text={t('coursePanel.startAllComputers')}
              />
            )}

            {shouldShowStopParticipantsMachines && (
              <ActionPosition
                onClick={onStopParticipantsMachines}
                icon={<RemoveCircleIcon style={{ color: Colors.green }} />}
                text={t('coursePanel.stopAllComputers')}
              />
            )}

            {shouldShowRemoveParticipantsMachines && (
              <ActionPosition
                onClick={onRemoveParticipantsMachines}
                icon={<DeleteIcon style={{ color: Colors.green }} />}
                text={t('coursePanel.deleteAllComputers')}
              />
            )}

            {shouldShowStartParticipantsMachines ||
              shouldShowStopParticipantsMachines ||
              (shouldShowRemoveParticipantsMachines && <Divider />)}

            {canEditCourse && (
              <ActionPosition
                onClick={handleEditCourseClick}
                icon={<EditIcon style={{ color: Colors.green }} />}
                text={t('coursePanel.editCourse')}
              />
            )}
          </Options>
        </BaseModal>
      )}

      {showJoinLink && (
        <HeadDialog
          onClose={toggleShowJoinLink}
          open={showJoinLink}
          content={
            <ShowLinkContent>
              <Typography variant={Typo.regular}>{t('coursePanel.showLinkFirstRow')}</Typography>
              <BigTypography>{joinLink}</BigTypography>
            </ShowLinkContent>
          }
          buttons={<Button onClick={handleCopyJoinLink}>{t('coursePanel.copyJoinLink')}</Button>}
        />
      )}
    </MainContainer>
  );
};
